import * as React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { isDateOnlyString, coerceToDate } from "./referralHelpers";

/**
 * DateStamp
 * - Renders date (and time if available) without truncation.
 * - Accepts: ISO string, epoch (sec/ms), Date, or YYYY-MM-DD (date-only).
 */
export function DateStamp({
  value,
  sx,
}: {
  value: any;
  sx?: SxProps<Theme>;
}) {
  if (!value && value !== 0) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  if (typeof value === "string" && isDateOnlyString(value)) {
    const [y, m, d] = value.split("-").map(Number);
    const asDate = new Date(Date.UTC(y, m - 1, d));
    const label = asDate.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
    return (
      <Tooltip title={value}>
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.2, ...(sx || {}) }}>
          <Typography variant="body2" sx={{ m: 0 }}>
            {label}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const d = coerceToDate(value);
  if (!d) {
    return (
      <Typography component="span" variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const datePart = d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());

  return (
    <Tooltip title={d.toISOString()}>
      <Box sx={{ whiteSpace: "normal", lineHeight: 1.2, ...(sx || {}) }}>
        <Typography variant="body2" sx={{ m: 0 }}>
          {datePart}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ m: 0 }}>
          {timePart}
        </Typography>
      </Box>
    </Tooltip>
  );
}


export function ChipWrap({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
        alignItems: "center",
        maxWidth: "100%",
      }}
    >
      {children}
    </Box>
  );
}
